import type {
  CallRecord,
  CallRecordListResponse,
  CallRecordsSortField,
  CallMatchStatus,
  CallReviewStatus,
  CallUrgency,
  CallType,
  CallOutcome,
  CallOrigin,
  CallDirection,
  CallKind,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export interface FetchCallsParams {
  pageSize: number;
  /**
   * Opaque forward cursor from the previous response's `nextCursor`; omit for
   * the first page. Dataverse has no `$skip`, so there is no page number to
   * send — see the contract note on `CallRecordListQuery.cursor`.
   */
  cursor?: string;
  sort?: CallRecordsSortField;  // default: 'startTime'
  order?: 'asc' | 'desc';       // default: 'desc'
  search?: string;
  from?: string;                // YYYY-MM-DD
  to?: string;                  // YYYY-MM-DD
  urgency?: CallUrgency;
  matchStatus?: CallMatchStatus;
  reviewStatus?: CallReviewStatus;
  callType?: CallType;
  outcome?: CallOutcome;
  /** `cdr` rows have no recording and so no transcript or summaries. */
  origin?: CallOrigin;
  direction?: CallDirection;
  callKind?: CallKind;
  callbackRequired?: boolean;
  credentialReview?: boolean;
  /** Only calls taken on the signed-in user's extension. */
  mine?: boolean;
  customerId?: string;
  jobId?: string;
  signal?: AbortSignal;
}

/**
 * Fetch a page of call records. Mirrors the envelope shape of `fetchInvoices` —
 * items + pagination metadata.
 *
 * Rows are `CallRecordListItem`: no transcript at any tier, and no management
 * summary unless the caller is Admin/Manager. Read one call with
 * `fetchCallDetail` to get the transcript.
 */
export async function fetchCalls(
  params: FetchCallsParams,
): Promise<CallRecordListResponse> {
  const qs = new URLSearchParams();
  qs.set('pageSize', String(params.pageSize));
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  if (params.search) qs.set('search', params.search);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.urgency) qs.set('urgency', params.urgency);
  if (params.matchStatus) qs.set('matchStatus', params.matchStatus);
  if (params.reviewStatus) qs.set('reviewStatus', params.reviewStatus);
  if (params.callType) qs.set('callType', params.callType);
  if (params.outcome) qs.set('outcome', params.outcome);
  if (params.origin) qs.set('origin', params.origin);
  if (params.direction) qs.set('direction', params.direction);
  if (params.callKind) qs.set('callKind', params.callKind);
  if (params.callbackRequired) qs.set('callbackRequired', 'true');
  if (params.credentialReview) qs.set('credentialReview', 'true');
  if (params.mine) qs.set('mine', 'true');
  if (params.customerId) qs.set('customerId', params.customerId);
  if (params.jobId) qs.set('jobId', params.jobId);

  const response = await fetch(`${API_BASE_URL}/calls?${qs}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal: params.signal,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch calls: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return {
    items: data.items ?? [],
    pagination: data.pagination ?? {
      pageSize: 50,
      totalItems: 0,
      totalPages: 0,
      nextCursor: null,
      hasNext: false,
    },
  };
}

/**
 * One call record, including the transcript when the signed-in user is Admin
 * or Manager. For anyone else the admin-tier keys are ABSENT from the response
 * (not null) — test with `in`, never assume presence.
 */
export async function fetchCallDetail(
  callId: string,
  signal?: AbortSignal,
): Promise<CallRecord> {
  const response = await fetch(
    `${API_BASE_URL}/calls/${encodeURIComponent(callId)}`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(
      `Failed to fetch call ${callId}: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
}
